import Layout from '@/components/layout/Layout'
import CustomSelectBase from '@/components/Returns/CustomSelect'

import { useSnackbar } from '@/contexts/SnackbarProvider'
import {
  createDuplicationRule,
  fetchDuplicationRuleParameterOptions,
  updateDuplicationRule,
  type DuplicationRuleRow,
} from '@/multi-territory/mt-services/mt-duplication-rule-api'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { z } from 'zod'

const CustomSelect = CustomSelectBase as any

const DUPLICATE_PARAM_MESSAGE =
  'This parameter is already selected. Please choose a different parameter'

// Case/whitespace-insensitive normalisation used for comparing parameters.
const normalizeKey = (s: string): string =>
  s.trim().replace(/\s+/g, ' ').toLowerCase()

// Aggressive match key used ONLY to resolve a stored parameter to a metadata
// field_key. The list API can return a parameter in several shapes:
//   - field_key:   "invoice_date_if_no_agreement_exist"
//   - metadata label: "Invoice Date (if no agreement exist)"
//   - de-underscored, title-cased key: "Invoice Date If No Agreement Exist"
// Stripping every non-alphanumeric char and lowercasing folds all three to the
// same token ("invoicedateifnoagreementexist") so matching succeeds.
const matchKey = (s: string): string =>
  s.toLowerCase().replace(/[^a-z0-9]/g, '')

// --- Zod Validation Schema ---
const duplicationRuleSchema = z.object({
  ruleName: z.string().trim().min(1, 'Rule name is required'),
  ruleStatus: z.literal('ACTIVE'),
  parameters: z
    .array(z.string().min(1, 'Parameter is required'))
    .min(1, 'At least one parameter is required')
    .max(8, 'You can add maximum 8 parameters')
    .superRefine((params, ctx) => {
      const seen = new Set<string>()
      params.forEach((value, index) => {
        const key = normalizeKey(value)
        if (seen.has(key)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: DUPLICATE_PARAM_MESSAGE,
            path: [index],
          })
        }
        seen.add(key)
      })
    }),
})

// --- Helper: Get duplicate errors ---
// `options` is optional so callers that already hold canonical values still work.
// A parameter may be held either as an option value ("INVOICE_NO") or, in edit
// mode, as a label ("Load Id"). We resolve it to its value, then fold the value
// AND its label into a case/whitespace-insensitive key so visually identical
// parameters (e.g. "Load Id" vs "Load ID") are treated as the same selection
// and the error shows immediately on the first duplicate selection.
function getDuplicateErrors(
  params: string[],
  options: Array<{ label: string; value: string }> = []
): Record<number, string> {
  const labelToValue = new Map<string, string>(
    options.map(o => [o.label, o.value])
  )
  const valueToLabel = new Map<string, string>(
    options.map(o => [o.value, o.label])
  )

  const canonicalKey = (p: string): string => {
    const value = labelToValue.get(p) ?? p
    const label = valueToLabel.get(value) ?? p
    return normalizeKey(label) || normalizeKey(value)
  }

  const seen = new Set<string>()
  const errors: Record<number, string> = {}
  params.forEach((value, index) => {
    if (!value) return
    const key = canonicalKey(value)
    if (seen.has(key)) {
      errors[index] = DUPLICATE_PARAM_MESSAGE
    } else {
      seen.add(key)
    }
  })
  return errors
}

// --- Helper: seed a loaded parameter ---
// The list API returns parameters as display LABELS (e.g. "Payee Id"), not
// field_keys. We can only convert them once the metadata options have loaded,
// so this just carries the raw value through; the resolution to field_key
// happens in resolveParamsToFieldKeys() once options arrive.
function mapLabelToValue(label: string | undefined): string {
  if (!label) return ''
  return label
}

// --- Helper: resolve parameters to metadata field_keys ---
// The backend expects field_keys. A parameter in state can be either a
// field_key (from the dropdown on add) or a display label (from the list API
// on edit). This resolves each to its metadata field_key by matching the
// options on field_key OR normalised label. Values that cannot be matched are
// returned unchanged so the caller can decide how to handle them.
function resolveParamsToFieldKeys(
  params: string[],
  options: Array<{ label: string; value: string }>
): string[] {
  const fieldKeySet = new Set(options.map(o => o.value))
  const lookup = new Map<string, string>()
  options.forEach(o => {
    lookup.set(matchKey(o.value), o.value) // field_key    -> field_key
    lookup.set(matchKey(o.label), o.value) // label        -> field_key
  })
  return params.map(p => {
    if (!p) return p
    if (fieldKeySet.has(p)) return p // already a field_key
    return lookup.get(matchKey(p)) ?? p // label/de-norm key -> field_key
  })
}

// --- Props ---
interface MTDuplicationRuleFormProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  editingRule: DuplicationRuleRow | null
  countryCode: string
  tanMasterUuid: string
  /** Whether TAN is applicable - determines identifier fields in payload */
  tanApplicable?: boolean
}

export function MTDuplicationRuleForm({
  open,
  onClose,
  onSuccess,
  editingRule,
  countryCode,
  tanMasterUuid,
  tanApplicable,
}: MTDuplicationRuleFormProps) {
  const isTanApplicable = tanApplicable ?? true
  const isEdit = !!editingRule

  // Initialize form data
  const initialParams = isEdit
    ? [
        editingRule.parameter_1,
        editingRule.parameter_2,
        editingRule.parameter_3,
        editingRule.parameter_4,
        editingRule.parameter_5,
        editingRule.parameter_6,
        editingRule.parameter_7,
        editingRule.parameter_8,
      ]
        .filter(Boolean)
        .map(p => mapLabelToValue(p))
    : ['']

  const [ruleName, setRuleName] = useState(editingRule?.ruleName || '')
  const [parameters, setParameters] = useState<string[]>(
    initialParams.length > 0 ? initialParams : ['']
  )
  const [formErrors, setFormErrors] = useState<Record<string | number, string>>(
    {}
  )
  const [saving, setSaving] = useState(false)

  const { showSnackbar } = useSnackbar()

  // Parameter options from API
  const [parameterOptions, setParameterOptions] = useState<any[]>([])

  // Reset form state when editingRule or open changes
  useEffect(() => {
    if (open) {
      const params = editingRule
        ? [
            editingRule.parameter_1,
            editingRule.parameter_2,
            editingRule.parameter_3,
            editingRule.parameter_4,
            editingRule.parameter_5,
            editingRule.parameter_6,
            editingRule.parameter_7,
            editingRule.parameter_8,
          ]
            .filter(Boolean)
            .map(p => mapLabelToValue(p))
        : ['']
      setRuleName(editingRule?.ruleName || '')
      setParameters(params.length > 0 ? params : [''])
      setFormErrors({})
      setSaving(false)
    }
  }, [open, editingRule])

  // Fetch parameter options from metadata API
  useEffect(() => {
    if (!countryCode || !open) return
    fetchDuplicationRuleParameterOptions(countryCode).then(result => {
      if (result.success && result.data) {
        const fields = (result.data as any)?.fields ?? result.data
        if (Array.isArray(fields)) {
          // A duplication rule should only match on columns the user actually
          // provides, so exclude system-generated/computed columns (AI
          // suggestions, variance, status, "Reference Number", "Duplication
          // Reference Number", "Excluded", etc.) and hidden columns. Guard
          // against malformed entries and de-duplicate by field_key.
          const seenKeys = new Set<string>()
          const options = fields
            .filter((item: any) => {
              if (!item || !item.field_key) return false
              if (item.system_generated === true) return false
              if (item.visible === false) return false
              if (seenKeys.has(item.field_key)) return false
              seenKeys.add(item.field_key)
              return true
            })
            .map((item: any) => ({
              label: item.label ?? item.field_key,
              value: item.field_key, // field_key — stored & sent to the API
            }))
          setParameterOptions(options)

          // As soon as the metadata options are available, convert the loaded
          // parameters (labels from the list API on edit) to their field_keys
          // using those same options. After this, `parameters` holds only
          // metadata field_keys for BOTH add and edit, so the save path sends
          // them directly.
          setParameters(prev => {
            const resolved = resolveParamsToFieldKeys(prev, options)
            const changed = resolved.some((v, i) => v !== prev[i])
            const next = changed ? resolved : prev
            setFormErrors(getDuplicateErrors(next, options))
            return next
          })
        }
      }
    })
  }, [countryCode, open])

  const handleAddParameter = () => {
    if (parameters.length >= 8) return
    setParameters([...parameters, ''])
  }

  const handleDeleteParameter = (index: number) => {
    const updated = parameters.filter((_, i) => i !== index)
    setParameters(updated)
    setFormErrors(getDuplicateErrors(updated, parameterOptions))
  }

  const handleParameterChange = (index: number, value: string) => {
    const updated = parameters.map((p, i) => (i === index ? value : p))
    setParameters(updated)
    setFormErrors(getDuplicateErrors(updated, parameterOptions))
  }

  const handleRuleNameChange = (value: string) => {
    setRuleName(value)
    if (formErrors.ruleName) {
      setFormErrors(prev => ({ ...prev, ruleName: '' }))
    }
  }

  const handleSave = async () => {
    // Guard: block save when any duplicate parameter is present, even if the
    // Zod path missed it (defensive — keeps UI errors and submit in sync).
    const duplicateErrors = getDuplicateErrors(parameters, parameterOptions)
    if (Object.keys(duplicateErrors).length > 0) {
      setFormErrors(prev => ({ ...prev, ...duplicateErrors }))
      return
    }

    const result = duplicationRuleSchema.safeParse({
      ruleName,
      ruleStatus: 'ACTIVE',
      parameters,
    })

    if (!result.success) {
      const newErrors: Record<string | number, string> = {}
      result.error.issues.forEach(err => {
        if (err.path[0] === 'parameters') {
          const index = err.path[1]
          newErrors[index as number] = err.message
        } else {
          newErrors[err.path[0] as string] = err.message
        }
      })
      setFormErrors(newErrors)
      return
    }

    // GUARANTEE: every parameter sent to the API must be a metadata field_key,
    // never a display label — in BOTH add and edit. Resolve against the same
    // metadata options, then verify. If any parameter is still not a real
    // field_key, block the save with an inline error instead of sending a label.
    const fieldKeySet = new Set<string>(
      parameterOptions.map((o: any) => o.value)
    )
    const fieldKeyParameters = resolveParamsToFieldKeys(
      result.data.parameters,
      parameterOptions
    )

    const resolveErrors: Record<number, string> = {}
    fieldKeyParameters.forEach((p, index) => {
      if (!fieldKeySet.has(p)) {
        resolveErrors[index] = 'Please re-select this parameter'
      }
    })

    if (Object.keys(resolveErrors).length > 0) {
      setFormErrors(prev => ({ ...prev, ...resolveErrors }))
      return
    }

    setFormErrors({})
    setSaving(true)

    // Build payload matching the backend API contract
    // tanApplicable=true -> taxMasterUuid=tanUuid, entityMasterUuid=null
    // tanApplicable=false -> entityMasterUuid=entityUuid, taxMasterUuid=null
    const payload = {
      ruleName: result.data.ruleName,
      tanMasterUuid: isTanApplicable ? tanMasterUuid : null,
      entityMasterUuid: isTanApplicable ? null : tanMasterUuid,
      countryCode,
      // Guaranteed field_keys only (verified against metadata above).
      parameters: fieldKeyParameters,
      ...(isEdit && { uuid: editingRule!.uuid }),
    }

    const apiResult = isEdit
      ? await updateDuplicationRule(payload)
      : await createDuplicationRule(payload)

    setSaving(false)

    if (apiResult.success) {
      showSnackbar(apiResult?.data, 'success')
      onSuccess()
    } else {
      showSnackbar(apiResult.error || 'Operation failed', 'error')
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullScreen maxWidth="lg">
      <Layout>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Typography variant="h6">
            {isEdit ? 'Edit Duplication Rule' : 'Add Duplication Rule'}
          </Typography>
        </Box>

        <DialogContent sx={{ p: 3, pb: 12 }}>
          <Typography
            variant="caption"
            color="error"
            sx={{ mb: 2, display: 'block' }}
          >
            (*) Fields are mandatory
          </Typography>

          {/* Rule Name + Status */}
          <Box
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mt: 2 }}
          >
            <Box sx={{ flex: 1, maxWidth: '400px' }}>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                Rule Name <span style={{ color: 'red' }}>*</span>
              </Typography>
              <input
                type="text"
                placeholder="Enter rule name"
                value={ruleName}
                onChange={e => handleRuleNameChange(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: formErrors.ruleName
                    ? '1px solid red'
                    : '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '14px',
                  outline: 'none',
                }}
              />
              {formErrors.ruleName && (
                <Typography variant="caption" color="error">
                  {formErrors.ruleName}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Parameters */}

          <Box sx={{ background: 'var(--bg-layer-1)', p: 2, mt: 2 }}>
            {parameters.map((param, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'flex-start',
                  maxWidth: '600px',
                  py: 1,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    Parameter {index + 1}{' '}
                    <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <CustomSelect
                    value={param}
                    onChange={(e: any) =>
                      handleParameterChange(index, e.target.value)
                    }
                    options={parameterOptions}
                    getOptionLabel={(opt: any) => opt.label}
                    getOptionValue={(opt: any) => opt.value}
                    placeholder="Select parameter"
                    error={!!formErrors[index]}
                    helperText={formErrors[index] || ''}
                    fullWidth
                  />
                </Box>

                {parameters.length > 1 && (
                  <IconButton
                    onClick={() => handleDeleteParameter(index)}
                    sx={{ color: 'var(--forbrand-primary)', mt: 3 }}
                    aria-label={`Delete parameter ${index + 1}`}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                )}
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddParameter}
              disabled={parameters.length >= 8}
              sx={{
                mt: 2,
                color: 'var(--forbrand-primary)',
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Add Parameter
            </Button>
          </Box>
        </DialogContent>

        {/* Bottom action bar */}
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: '#fff',
            borderTop: '1px solid #e0e0e0',
            py: 2,
            px: 3,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            zIndex: 1,
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              textTransform: 'none',
              color: 'var(--forbrand-primary)',
              border: '1px solid var(--border-subtel)',
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              backgroundColor: 'var(--forbrand-bg-variant, #1976d2)',
              color: 'var(--forbrand-brand-varient, #fff)',
              textTransform: 'none',
              fontSize: '16px',
            }}
          >
            {isEdit ? 'Update' : 'Save'}
          </Button>
        </Box>
      </Layout>
    </Dialog>
  )
}
